
import { Paper } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { useMemo } from "react";

const useStyles = makeStyles({
  root: {
    border: 0,
    color: 'text.primary',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    backgroundColor: 'transparent'
  },
  tableHeading: {
    marginLeft: 10,
    marginTop: 10,
  },
  table: {
    overflow: 'auto',
    marginTop: 0,
    marginBottom: 0,
    border: 'none'
  },
});

export const RTFOStepdownDataTable = ({ currentRTFO, darkMode, sdtType='flush' }) => {

  
    // STYLES
    const classes = useStyles();

    const rows = useMemo(() => {
      if (!currentRTFO?.stageData || Object.keys(currentRTFO.stageData)?.length === 0 || currentRTFO?.stages?.length === 0) return [];

      return [...currentRTFO.stages]?.reverse()?.map(stageName => {
        const stage = currentRTFO?.stageData?.[stageName];
        const stepdownData = sdtType === 'flush' ? stage?.stepdownData : stage?.preSandStepdownData;
        const stepdownParams = stage?.stepdownParams;

        return {
          id: stageName,
          stageName,
          wellName: stage?.wellName,
          stageNumber: (stage?.stageNumber), 
          pressure0: Number(stepdownData?.[0]?.pressure?.toFixed(3)) || 0,
          rate0: Number(stepdownData?.[0]?.rate?.toFixed(3)) || 0,
          pPerf0: Number(stepdownData?.[0]?.pPerf?.toFixed(3)) || 0,
          pPipe0: Number(stepdownData?.[0]?.pPipe?.toFixed(3)) || 0,
          pNwb0: Number(stepdownData?.[0]?.pNwb?.toFixed(3)) || 0,

          pressure1: Number(stepdownData?.[1]?.pressure?.toFixed(3)) || 0,
          rate1: Number(stepdownData?.[1]?.rate?.toFixed(3)) || 0,
          pPerf1: Number(stepdownData?.[1]?.pPerf?.toFixed(3)) || 0,
          pPipe1: Number(stepdownData?.[1]?.pPipe?.toFixed(3)) || 0,
          pNwb1: Number(stepdownData?.[1]?.pNwb?.toFixed(3)) || 0,

          pressure2: Number(stepdownData?.[2]?.pressure?.toFixed(3)) || 0,
          rate2: Number(stepdownData?.[2]?.rate?.toFixed(3)) || 0,
          pPerf2: Number(stepdownData?.[2]?.pPerf?.toFixed(3)) || 0,
          pPipe2: Number(stepdownData?.[2]?.pPipe?.toFixed(3)) || 0,
          pNwb2: Number(stepdownData?.[2]?.pNwb?.toFixed(3)) || 0,
          
          pressure3: Number(stepdownData?.[3]?.pressure?.toFixed(3)) || 0,
          rate3: Number(stepdownData?.[3]?.rate?.toFixed(3)) || 0,
          pPerf3: Number(stepdownData?.[3]?.pPerf?.toFixed(3)) || 0,
          pPipe3: Number(stepdownData?.[3]?.pPipe?.toFixed(3)) || 0,
          pNwb3: Number(stepdownData?.[3]?.pNwb?.toFixed(3)) || 0,
          
          pressure4: Number(stepdownData?.[4]?.pressure?.toFixed(3)) || 0,
          rate4: Number(stepdownData?.[4]?.rate?.toFixed(3)) || 0,
          pPerf4: Number(stepdownData?.[4]?.pPerf?.toFixed(3)) || 0,
          pPipe4: Number(stepdownData?.[4]?.pPipe?.toFixed(3)) || 0,
          pNwb4: Number(stepdownData?.[4]?.pNwb?.toFixed(3)) || 0,

          pNwb: Number(stepdownParams?.pNwb?.toFixed(3)) || 0,
          pPerf: Number(stepdownParams?.pPerf?.toFixed(3)) || 0,
          isip: Number(stepdownParams?.isip?.toFixed(3)) || 0,
          topDepth: Number(stepdownParams?.topDepth?.toFixed(3)) || 0,
          bottomDepth: Number(stepdownParams?.bottomDepth?.toFixed(3)) || 0,
          averageDepth: Number(stepdownParams?.averageDepth?.toFixed(3)) || 0,
          fc: Number(stepdownParams?.fcFrictionModel?.toFixed(4)) || 0,
          plExp: Number(stepdownParams?.plExpFrictionModel?.toFixed(4)) || 0
        };
      });
    }, [currentRTFO?.stageData, currentRTFO?.stages, sdtType]);
  
  try {

    const columns = [
      {
        field: 'stageName',
        headerName: 'Stage Name',
        width: 150,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'string'
      },
      {
        field: 'wellName',
        headerName: 'Well',
        width: 100,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'string'
      },
      {
        field: 'stageNumber',
        headerName: 'Stage No.',
        width: 100,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'pressure0',
        headerName: 'Pmax (MPa)',
        width: 100,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'rate0',
        headerName: 'Qmax (m3/min)',
        width: 100,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'pPerf0',
        headerName: 'Pperf 0 (MPa)',
        width: 100,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'pPipe0',
        headerName: 'Ppipe 0 (MPa)',
        width: 100,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'pNwb0',
        headerName: 'Pnwb0 (MPa)',
        width: 100,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'pressure1',
        headerName: 'P1 (MPa)',
        width: 100,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'rate1',
        headerName: 'Q1 (m3/min)',
        width: 100,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'pPerf1',
        headerName: 'Pperf 1 (MPa)',
        width: 100,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'pPipe1',
        headerName: 'Ppipe 1 (MPa)',
        width: 100,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'pNwb1',
        headerName: 'Pnwb 1 (MPa)',
        width: 100,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'pressure2',
        headerName: 'P2 (MPa)',
        width: 100,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'rate2',
        headerName: 'Q2 (m3/min)',
        width: 100,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'pPerf2',
        headerName: 'Pperf 2 (MPa)',
        width: 100,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'pPipe2',
        headerName: 'Ppipe 2 (MPa)',
        width: 100,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'pNwb2',
        headerName: 'Pnwb 2 (MPa)',
        width: 100,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'pressure3',
        headerName: 'P3 (MPa)',
        width: 100,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'rate3',
        headerName: 'Q3 (m3/min)',
        width: 100,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'pPerf3',
        headerName: 'Pperf 3 (MPa)',
        width: 100,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'pPipe3',
        headerName: 'Ppipe 3 (MPa)',
        width: 100,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'pNwb3',
        headerName: 'Pnwb 3 (MPa)',
        width: 100,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'pressure4',
        headerName: 'P4 (MPa)',
        width: 100,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'rate4',
        headerName: 'Q4 (m3/min)',
        width: 100,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'pPerf4',
        headerName: 'Pperf 4 (MPa)',
        width: 100,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'pPipe4',
        headerName: 'Ppipe 4 (MPa)',
        width: 100,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'pNwb4',
        headerName: 'Pnwb 4 (MPa)',
        width: 100,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'pNwb',
        headerName: 'Pnwb (MPa)',
        width: 100,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'pPerf',
        headerName: 'Pperf (MPa)',
        width: 100,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'isip',
        headerName: 'ISIP (MPa)',
        width: 100,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'topDepth',
        headerName: 'Cluster Top (m)',
        width: 100,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'bottomDepth',
        headerName: 'Cluster Bottom (m)',
        width: 100,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'averageDepth',
        headerName: 'Depth of Calculation (m)',
        width: 100,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'fc',
        headerName: 'PL Coeff.',
        width: 100,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'plExp',
        headerName: 'PL Exp.',
        width: 100,
        editable: false,
        sortable: true,
        filterable: true
      },
    ];

    return (
      <Paper className={classes.root} elevation={0}>
        <DataGrid
          rows={rows}
          columns={columns}
          rowsPerPageOptions={[10,25,50,100]}
          disableSelectionOnClick
          className={classes.table}
          components={{
            Toolbar: GridToolbar,
          }}
          sx={{
            '& .MuiDataGrid-row:nth-of-type(odd)': {
              backgroundColor: darkMode ? 'grey' : 'whitesmoke'
            },
            '& .super-app-theme--Current': {
              fontWeight: 'bold',
              backgroundColor: 'rgba(7, 88, 183, 0.1)' // highlight the current row
            }
          }}
          getRowClassName={(params) => {
            return (params?.row?.stageName === currentRTFO?.currentStageName) ? `super-app-theme--Current` : '';
          }}
          density="compact"
          style={{fontSize: '12px'}}
        />
      </Paper>
    );
  } catch (error) {
    return <></>
  };
};

